// src/pages/ExerciseListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Snackbar,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ActivateIcon,
  Stop as CloseIcon,
  Refresh as RefreshIcon,
  ContentCopy as CloneIcon,
  PeopleAlt as PeopleIcon,
  Undo as ReturnIcon,
  LockOpen as ReopenIcon,
} from '@mui/icons-material';
import {
  getAllExercises,
  deleteExercise,
  activateExercise,
  closeExercise,
} from '../services/exerciseService';
import api from '../services/api';
import CreateExerciseDialog from '../components/CreateExerciseDialog';
import EditExerciseDialog from '../components/EditExerciseDialog';
import CloneCommandsDialog from '../components/CloneCommandsDialog';
import ClonePersonnelDialog from '../components/ClonePersonnelDialog';

import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';

const ExerciseListPage = () => {
  const navigate = useNavigate();

  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);

  // Clone dialogs
  const [cloneCmdDialogOpen, setCloneCmdDialogOpen] = useState(false);
  const [clonePplDialogOpen, setClonePplDialogOpen] = useState(false);
  const [exerciseToCloneInto, setExerciseToCloneInto] = useState(null);

  // Lifecycle confirmations
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [exerciseToActivate, setExerciseToActivate] = useState(null);
  const [exerciseToClose, setExerciseToClose] = useState(null);
  const [exerciseToReturn, setExerciseToReturn] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Delete confirmation (type-to-confirm)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchExercises = useCallback(async () => {
    setError('');
    try {
      const response = await getAllExercises();
      setExercises(response.data.exercises);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load exercises');
    }
  }, []);

  const [initialLoad, setInitialLoad] = useState(true);
  useEffect(() => {
    const load = async () => {
      setError('');
      try {
        const response = await getAllExercises();
        setExercises(response.data.exercises);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load exercises');
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    if (initialLoad) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (exercise) => {
    setSelectedExercise(exercise);
    setEditDialogOpen(true);
  };

  const handleCloneCommandsClick = (exercise) => {
    setExerciseToCloneInto(exercise);
    setCloneCmdDialogOpen(true);
  };
  const handleClonePersonnelClick = (exercise) => {
    setExerciseToCloneInto(exercise);
    setClonePplDialogOpen(true);
  };

  const handleDeleteClick = (exercise) => {
    setExerciseToDelete(exercise);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setError('');
    const name = exerciseToDelete?.name;
    try {
      await deleteExercise(exerciseToDelete.id);
      setDeleteDialogOpen(false);
      setExerciseToDelete(null);
      await fetchExercises();
      notify(`Exercise "${name}" deleted successfully`, 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete exercise';
      setError(msg);
      notify(msg, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleActivateClick = (exercise) => {
    setExerciseToActivate(exercise);
    setActivateDialogOpen(true);
  };
  const handleActivateConfirm = async () => {
    setActionLoading(true);
    setError('');
    const name = exerciseToActivate?.name;
    const reopen = exerciseToActivate?.status === 'CLOSED';
    try {
      await activateExercise(exerciseToActivate.id);
      setActivateDialogOpen(false);
      setExerciseToActivate(null);
      await fetchExercises();
      notify(`Exercise "${name}" ${reopen ? 're-opened' : 'activated'} successfully`, 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to activate exercise';
      setError(msg);
      notify(msg, 'error');
      setActivateDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseClick = (exercise) => {
    setExerciseToClose(exercise);
    setCloseDialogOpen(true);
  };
  const handleCloseConfirm = async () => {
    setActionLoading(true);
    setError('');
    const name = exerciseToClose?.name;
    try {
      await closeExercise(exerciseToClose.id);
      setCloseDialogOpen(false);
      setExerciseToClose(null);
      await fetchExercises();
      notify(`Exercise "${name}" closed successfully`, 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to close exercise';
      setError(msg);
      notify(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReturnClick = (exercise) => {
    setExerciseToReturn(exercise);
    setReturnDialogOpen(true);
  };
  const handleReturnConfirm = async () => {
    setActionLoading(true);
    setError('');
    const name = exerciseToReturn?.name;
    try {
      await api.post(`/exercises/${exerciseToReturn.id}/return-to-draft`);
      setReturnDialogOpen(false);
      setExerciseToReturn(null);
      await fetchExercises();
      notify(`Exercise "${name}" returned to draft`, 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to return exercise to draft';
      setError(msg);
      notify(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'DRAFT': return 'info';
      case 'PLANNED': return 'info';
      case 'CLOSED': return 'default';
      case 'COMPLETED': return 'default';
      default: return 'default';
    }
  };

  const columns = [
    { id: 'year', label: 'Year', render: (row) => <strong>{row?.year || '-'}</strong> },
    { id: 'name', label: 'Name', render: (row) => row?.name || '-' },
    {
      id: 'startDate', label: 'Start Date',
      render: (row) => (row?.startDate ? new Date(row.startDate).toLocaleDateString() : '-'),
    },
    {
      id: 'status', label: 'Status',
      render: (row) => row?.status
        ? <Chip label={row.status} color={getStatusColor(row.status)} size="small" />
        : '-',
    },
    { id: 'commands', label: 'Commands', render: (row) => row?._count?.commands || 0 },
    { id: 'personnel', label: 'Personnel', render: (row) => row?._count?.personnel || 0 },
  ];

  const rowActions = (row) => {
    const actions = [];

    if (row.status === 'DRAFT') {
      actions.push(
        { icon: <ActivateIcon fontSize="small" />, label: 'Activate', color: 'success', onClick: () => handleActivateClick(row) },
        { icon: <CloneIcon fontSize="small" />, label: 'Clone Commands', onClick: () => handleCloneCommandsClick(row) },
        { icon: <PeopleIcon fontSize="small" />, label: 'Clone Personnel', onClick: () => handleClonePersonnelClick(row) },
        { icon: <EditIcon fontSize="small" />, label: 'Edit', onClick: () => handleEdit(row) },
        { icon: <DeleteIcon fontSize="small" />, label: 'Delete', color: 'error', onClick: () => handleDeleteClick(row) }
      );
    } else if (row.status === 'ACTIVE') {
      actions.push({ icon: <CloseIcon fontSize="small" />, label: 'Close', color: 'warning', onClick: () => handleCloseClick(row) });
      // Return to Draft only if it has never been closed
      if (!row.wasClosed) {
        actions.push({ icon: <ReturnIcon fontSize="small" />, label: 'Return to Draft', onClick: () => handleReturnClick(row) });
      }
      actions.push({ icon: <EditIcon fontSize="small" />, label: 'Edit', onClick: () => handleEdit(row) });
    } else if (row.status === 'CLOSED') {
      // Re-open (becomes ACTIVE). No delete ever.
      actions.push(
        { icon: <ReopenIcon fontSize="small" />, label: 'Re-open', color: 'success', onClick: () => handleActivateClick(row) },
        { icon: <EditIcon fontSize="small" />, label: 'Edit', onClick: () => handleEdit(row) }
      );
    }

    return actions;
  };

  const deleteNameMatches =
    exerciseToDelete && deleteConfirmText.trim() === exerciseToDelete.name;

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <PageHeader
          title="Exercise Management"
          onBack={() => navigate('/')}
          actions={[
            { label: 'Refresh', icon: <RefreshIcon />, onClick: fetchExercises, variant: 'outlined' },
            { label: 'Create Exercise', icon: <AddIcon />, onClick: () => setCreateDialogOpen(true), variant: 'contained' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <DataTable
          columns={columns}
          data={exercises}
          loading={loading}
          rowActions={rowActions}
          emptyType="exercises"
          emptyMessage="No exercises found. Create your first exercise to get started."
        />

        {!loading && exercises.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total exercises: <strong>{exercises.length}</strong>
            </Typography>
          </Box>
        )}
      </Box>

      <CreateExerciseDialog
        open={createDialogOpen}
        onNotify={notify}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => { setCreateDialogOpen(false); fetchExercises(); }}
      />

      {selectedExercise && (
        <EditExerciseDialog
          open={editDialogOpen}
          exercise={selectedExercise}
          onNotify={notify}
          onClose={() => { setEditDialogOpen(false); setSelectedExercise(null); }}
          onSuccess={() => { setEditDialogOpen(false); setSelectedExercise(null); fetchExercises(); }}
        />
      )}

      {exerciseToCloneInto && cloneCmdDialogOpen && (
        <CloneCommandsDialog
          open={cloneCmdDialogOpen}
          targetExercise={exerciseToCloneInto}
          allExercises={exercises}
          onNotify={notify}
          onClose={() => { setCloneCmdDialogOpen(false); setExerciseToCloneInto(null); }}
          onSuccess={() => { setCloneCmdDialogOpen(false); setExerciseToCloneInto(null); fetchExercises(); }}
        />
      )}

      {exerciseToCloneInto && clonePplDialogOpen && (
        <ClonePersonnelDialog
          open={clonePplDialogOpen}
          targetExercise={exerciseToCloneInto}
          allExercises={exercises}
          onNotify={notify}
          onClose={() => { setClonePplDialogOpen(false); setExerciseToCloneInto(null); }}
          onSuccess={() => { setClonePplDialogOpen(false); setExerciseToCloneInto(null); fetchExercises(); }}
        />
      )}

      {/* Delete Confirmation — type-to-confirm, shows blast radius */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Exercise</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete the draft exercise{' '}
            <strong>{exerciseToDelete?.name}</strong>, including its{' '}
            <strong>{exerciseToDelete?._count?.commands || 0}</strong> command(s),{' '}
            <strong>{exerciseToDelete?._count?.personnel || 0}</strong> personnel,
            and all related verification records, pre-clearances and complaints.
            This cannot be undone.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type the exercise name to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={exerciseToDelete?.name || ''}
            disabled={deleteLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading || !deleteNameMatches}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate / Re-open Confirmation */}
      <Dialog open={activateDialogOpen} onClose={() => !actionLoading && setActivateDialogOpen(false)}>
        <DialogTitle>{exerciseToActivate?.status === 'CLOSED' ? 'Re-open Exercise' : 'Activate Exercise'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {exerciseToActivate?.status === 'CLOSED' ? 'Re-open' : 'Activate'}{' '}
            <strong>{exerciseToActivate?.name}</strong>?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Only one exercise can be active at a time. If another is active, you'll
            need to close or return it to draft first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleActivateConfirm} color="success" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : (exerciseToActivate?.status === 'CLOSED' ? 'Re-open' : 'Activate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Confirmation */}
      <Dialog open={closeDialogOpen} onClose={() => !actionLoading && setCloseDialogOpen(false)}>
        <DialogTitle>Close Exercise</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Close <strong>{exerciseToClose?.name}</strong>?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This marks the exercise as a permanent, closed record. It will stop
            accepting verifications. A closed exercise can be re-opened later but
            can never be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleCloseConfirm} color="warning" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Close Exercise'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return to Draft Confirmation */}
      <Dialog open={returnDialogOpen} onClose={() => !actionLoading && setReturnDialogOpen(false)}>
        <DialogTitle>Return to Draft</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Return <strong>{exerciseToReturn?.name}</strong> to draft?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This frees the active slot and makes the exercise editable and
            deletable again. Use this for practice exercises you intend to rework
            or discard. (Real exercises should be Closed instead.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleReturnConfirm} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Return to Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExerciseListPage;